import React, { useState, useEffect } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Divider,
  Stack,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Autocomplete,
  Tooltip
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Inventory2 as Inventory2Icon
} from '@mui/icons-material';
import * as resourceService from '../services/resourceService';
import ProjectFilesTab from './files/ProjectFilesTab';
import ProjectMoodboardTab from './files/ProjectMoodboardTab';
import LightingDiagramTool from './lighting/LightingDiagramTool';
import { FEATURE_FLAGS } from '../config';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import eventEmitter, { EVENTS } from '../utils/eventEmitter';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s ease',
  border: '1px solid',
  borderColor: alpha(theme.palette.divider, 0.2),
  overflow: 'hidden',
  height: 'auto',
  minHeight: 100,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    borderColor: alpha(theme.palette.divider, 0.4)
  }
}));

const StyledCardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  backgroundColor: theme.palette.background.paper
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: theme.spacing(1, 1, 0.5, 1),
  marginBottom: theme.spacing(0.5),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: theme.spacing(1),
    right: theme.spacing(1),
    height: 1,
    background: theme.palette.divider,
    opacity: 0.5
  }
}));

const CategoryHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? '#1e2124' : '#f5f5f7',
  borderRadius: theme.shape.borderRadius,
  borderLeft: `4px solid ${theme.palette.primary.main}`
}));

const ResourceGrid = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: alpha(theme.palette.background.default, 0.5),
  borderRadius: theme.shape.borderRadius
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  letterSpacing: 0.5,
  padding: theme.spacing(1.25, 3),
  borderRadius: theme.shape.borderRadius
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
  }
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: '0.9rem',
  padding: theme.spacing(0.25, 0, 0, 1),
  lineHeight: 1.2
}));

const DetailRow = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const DetailText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  lineHeight: 1.1
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    backgroundImage: 'none'
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.25rem',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3)
}));

const StyledFormField = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));

const ListItemContainer = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.action.active
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}));

const ListContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius
}));

const ListItemMainContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexGrow: 1
}));

const ListItemSecondaryContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

// Predefined resource types and their associated options
const RESOURCE_TYPES = {
  'Studio': ['Studio A', 'Studio B', 'Studio C'],
  'Equipment': ['Camera', 'Lighting', 'Sound', 'Grip'],
  'Post-Production': ['Editing Suite', 'Color Grading', 'Sound Mixing'],
  'Meeting Room': ['Conference Room A', 'Conference Room B', 'Screening Room'],
  'Hair & Makeup': ['Makeup Station', 'Hair Station', 'Prosthetics'],
  'Wardrobe': ['Costume Room', 'Fitting Room', 'Storage'],
  'Custom': ['Other'],
  'Vehicles': ['Truck', 'Car', 'Van'],
  'Props': ['Props Room', 'Props Storage'],
  'Set Dressing': ['Set A', 'Set B', 'Set C'],
  'Catering': ['Craft Services', 'Meals']
};

const RESOURCE_DETAILS = {
  'Studio': {
    icon: '🎬',
    fields: [], // Removed capacity, location, and equipment fields
    description: 'Production and recording spaces'
  },
  'Equipment': {
    icon: '🎥',
    fields: ['model', 'serialNumber', 'purchaseDate', 'warranty'],
    description: 'Production equipment and gear'
  },
  'Post-Production': {
    icon: '🖥️',
    fields: ['software', 'specifications', 'licenses'],
    description: 'Post-production facilities and software'
  },
  'Meeting Room': {
    icon: '🏢',
    fields: ['capacity', 'amenities', 'location'],
    description: 'Meeting and presentation spaces'
  },
  'Hair & Makeup': {
    icon: '💄',
    fields: ['capacity', 'amenities', 'location'],
    description: 'Hair and makeup facilities'
  },
  'Wardrobe': {
    icon: '👗',
    fields: ['capacity', 'amenities', 'location'],
    description: 'Wardrobe and costume facilities'
  },
  
  'Custom': {
    icon: '📦',
    fields: ['specifications', 'details'],
    description: 'Custom resource type'
  },
  'Vehicles': {
    icon: '🚐',
    fields: ['make', 'model', 'plate', 'driver', 'fuelType'],
    description: 'Production vehicles and transportation'
  },
  'Props': {
    icon: '🎭',
    fields: ['propMaster', 'condition', 'acquisitionDate', 'value'],
    description: 'Props inventory tracking'
  },
  'Set Dressing': {
    icon: '🖼️',
    fields: ['setDecorator', 'scene', 'requirements', 'status'],
    description: 'Set dressing requirements'
  },
  'Catering': {
    icon: '🍽️',
    fields: ['vendor', 'meals', 'dietaryRestrictions', 'contact'],
    description: 'Craft services and meals'
  },
};

const groupResourcesByType = (resources) => {
  return resources.reduce((acc, resource) => {
    // Hide legacy Locations from Resource page; managed in Callsheet now
    if (resource.type === 'Locations') return acc;
    const type = resource.type || 'Uncategorized';
    if (!acc[type]) {
      acc[type] = {
        name: type,
        icon: RESOURCE_DETAILS[type]?.icon || '📁',
        resources: []
      };
    }
    acc[type].resources.push(resource);
    return acc;
  }, {});
};

function ResourceManagement({ projectId }) {
  const theme = useTheme();
  const [resources, setResources] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('resources');
  const [selectedType, setSelectedType] = useState('');
  // Filters & sorting state
  const [query, setQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('name'); // 'name' | 'type' | 'date'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    subtype: '',
    status: 'pending_approval',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    allDay: true,
    startTime: '09:00',
    endTime: '17:00',
    location: '',
    capacity: '',
    equipment: '',
    model: '',
    serialNumber: '',
    purchaseDate: '',
    warranty: '',
    software: '',
    specifications: '',
    licenses: '',
    amenities: '',
    details: '',
    notes: '',
    make: '',
    plate: '',
    driver: '',
    fuelType: '',
    propMaster: '',
    condition: '',
    acquisitionDate: '',
    value: '',
    setDecorator: '',
    scene: '',
    requirements: '',
    status: '',
    vendor: '',
    meals: '',
    dietaryRestrictions: '',
    contact: ''
  });
  const [collapsedCategories, setCollapsedCategories] = useState({});

  const toggleCategory = (type) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Filtering and sorting helpers (scoped to component state)
  const typeOptions = Object.keys(RESOURCE_TYPES);

  const applyFilterSort = (list) => {
    let out = [...(list || [])];
    const q = (query || '').trim().toLowerCase();
    if (q) {
      out = out.filter(r => {
        const hay = `${r.name || ''} ${r.type || ''} ${r.subtype || ''} ${r.notes || ''}`.toLowerCase();
        return hay.includes(q);
      });
    }
    if (filterType !== 'all') {
      out = out.filter(r => (r.type || '') === filterType);
    }
    // Subtype filter removed (Option A)
    if (filterStatus !== 'all') {
      out = out.filter(r => (r.status || '') === filterStatus);
    }
    out.sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case 'type': va = `${a.type || ''} ${a.subtype || ''}`.toLowerCase(); vb = `${b.type || ''} ${b.subtype || ''}`.toLowerCase(); break;
        case 'date': va = new Date(a.startDate || a.createdAt || 0).getTime(); vb = new Date(b.startDate || b.createdAt || 0).getTime(); break;
        default: va = (a.name || '').toLowerCase(); vb = (b.name || '').toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return out;
  };

  useEffect(() => {
    console.log('ResourceManagement mounted with projectId:', projectId);
    if (projectId) {
      // Check for pending resource updates from localStorage
      const updateFlag = localStorage.getItem('resourceUpdateFlag');
      if (updateFlag) {
        try {
          const flagData = JSON.parse(updateFlag);
          console.log('ResourceManagement found update flag:', flagData);
          
          // Check if the update is for this project and is recent (within last 5 minutes)
          const isRecentUpdate = (Date.now() - flagData.timestamp) < 5 * 60 * 1000;
          const isForThisProject = flagData.projectId === projectId;
          
          if (isRecentUpdate && isForThisProject) {
            console.log('✅ ResourceManagement applying pending update for this project');
            // Clear the flag since we're handling it
            localStorage.removeItem('resourceUpdateFlag');
          } else {
            console.log('❌ Update flag not applicable:', { isRecentUpdate, isForThisProject });
          }
        } catch (error) {
          console.error('Error parsing update flag:', error);
          localStorage.removeItem('resourceUpdateFlag');
        }
      }
      
      fetchResources();
    }

    // Listen for resource updates from other components (e.g., Dashboard)
    const handleResourceUpdate = (eventData) => {
      console.log('ResourceManagement received resource update event:', eventData);
      console.log('ResourceManagement current projectId:', projectId);
      console.log('Event projectId:', eventData.projectId);
      console.log('ProjectId match:', eventData.projectId === projectId);
      console.log('ProjectId types:', typeof eventData.projectId, typeof projectId);
      
      // If the updated resource belongs to this project, refresh the resources
      if (eventData.projectId === projectId) {
        console.log('✅ Refreshing resources due to external update');
        fetchResources();
      } else {
        console.log('❌ Not refreshing - projectId mismatch');
      }
    };

    // Subscribe to resource update events
    console.log('ResourceManagement setting up event listeners for projectId:', projectId);
    console.log('Available EVENTS:', EVENTS);
    eventEmitter.on(EVENTS.RESOURCE_UPDATED, handleResourceUpdate);
    eventEmitter.on(EVENTS.RESOURCE_CREATED, handleResourceUpdate);
    eventEmitter.on(EVENTS.RESOURCE_DELETED, handleResourceUpdate);
    console.log('ResourceManagement event listeners set up successfully');

    // Cleanup event listeners on unmount
    return () => {
      eventEmitter.off(EVENTS.RESOURCE_UPDATED, handleResourceUpdate);
      eventEmitter.off(EVENTS.RESOURCE_CREATED, handleResourceUpdate);
      eventEmitter.off(EVENTS.RESOURCE_DELETED, handleResourceUpdate);
    };
  }, [projectId]);

  // Add a separate useEffect to handle page visibility changes
  // This will refresh resources when the user navigates back to the Resources page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && projectId) {
        console.log('ResourceManagement page became visible, refreshing resources');
        fetchResources();
      }
    };

    const handleFocus = () => {
      if (projectId) {
        console.log('ResourceManagement window focused, refreshing resources');
        fetchResources();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [projectId]);

  const fetchResources = async () => {
    try {
      console.log('Fetching resources for project:', projectId);
      const data = await resourceService.getProjectResources(projectId);
      console.log('Resources fetched:', data);
      setResources(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setError('Failed to load resources. Please try again later.');
    }
  };

  const handleOpenDialog = (resource = null) => {
    if (resource) {
      // Normalize dates for date inputs (expects YYYY-MM-DD)
      const toDateOnly = (v) => (v ? String(v).slice(0, 10) : '');
      const toTimeOnly = (v) => {
        if (!v) return '';
        const s = String(v);
        // If ISO, extract HH:MM; if already HH:MM, return as-is
        if (s.includes('T')) return s.slice(11, 16);
        if (/^\d{2}:\d{2}$/.test(s)) return s;
        return '';
      };
      const normalized = {
        ...resource,
        startDate: toDateOnly(resource.startDate) || new Date().toISOString().split('T')[0],
        endDate: toDateOnly(resource.endDate) || toDateOnly(resource.startDate) || new Date().toISOString().split('T')[0],
        allDay: resource.allDay !== undefined ? !!resource.allDay : true,
        startTime: toTimeOnly(resource.startTime) || '09:00',
        endTime: toTimeOnly(resource.endTime) || '17:00',
      };
      setFormData(normalized);
      setSelectedType(resource.type);
      setEditingResource(resource);
    } else {
      setFormData({
        name: '',
        type: '',
        subtype: '',
        status: 'pending_approval',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        allDay: true,
        startTime: '09:00',
        endTime: '17:00',
        location: '',
        capacity: '',
        equipment: '',
        model: '',
        serialNumber: '',
        purchaseDate: '',
        warranty: '',
        software: '',
        specifications: '',
        licenses: '',
        amenities: '',
        details: '',
        notes: '',
        make: '',
        plate: '',
        driver: '',
        fuelType: '',
        propMaster: '',
        condition: '',
        acquisitionDate: '',
        value: '',
        setDecorator: '',
        scene: '',
        requirements: '',
        vendor: '',
        meals: '',
        dietaryRestrictions: '',
        contact: ''
      });
      setSelectedType('');
      setEditingResource(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingResource(null);
    setSelectedType('');
  };

  const handleTypeChange = (event) => {
    const type = event.target.value;
    setSelectedType(type);
    setFormData(prev => ({
      ...prev,
      type: type,
      subtype: '',
      name: ''
    }));
  };

  const handleSubtypeChange = (event) => {
    const subtype = event.target.value;
    setFormData(prev => ({
      ...prev,
      subtype: subtype,
      name: selectedType === 'Custom' ? '' : subtype
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      // If updating endDate, ensure it's not before startDate
      if (name === 'endDate' && value < prev.startDate) {
        return prev;
      }
      // If updating startDate, ensure it's not after endDate
      if (name === 'startDate' && value > prev.endDate) {
        const newEndDate = value;
        return {
          ...prev,
          [name]: value,
          endDate: newEndDate
        };
      }
      // Time validations when not all-day and same date
      if (!prev.allDay && prev.startDate === prev.endDate) {
        if (name === 'endTime' && prev.startTime && value < prev.startTime) {
          // Prevent end before start on same day
          return { ...prev, endTime: prev.startTime };
        }
        if (name === 'startTime' && prev.endTime && value > prev.endTime) {
          // Push endTime forward to match startTime
          return { ...prev, startTime: value, endTime: value };
        }
      }
      return {
        ...prev,
        [name]: value
      };
    });
  };

  const handleSubmit = async () => {
    try {
      // Basic validation
      if (!formData.name || !formData.type) {
        setError('Please fill in all required fields');
        return;
      }

      // Prepare data to send
      const dataToSend = {
        ...formData,
        projectId: projectId
      };

      console.log('Submitting resource data:', dataToSend);

      if (editingResource) {
        await resourceService.updateResource(projectId, editingResource._id, dataToSend);
      } else {
        await resourceService.createResource(projectId, dataToSend);
      }
      fetchResources();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving resource:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save resource. Please try again later.';
      setError(errorMessage);
    }
  };

  const handleDelete = async (resourceId) => {
    try {
      await resourceService.deleteResource(projectId, resourceId);
      fetchResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      setError('Failed to delete resource. Please try again later.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_approval':
        return 'secondary';
      case 'accepted':
        return 'success';
      case 'denied':
        return 'error';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 64px)' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Compact Header with Tabs */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        py: 0.5,
        px: 1
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Inventory2Icon sx={{ color: 'primary.main', fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle1" sx={{
            fontWeight: 600,
            color: (theme) => theme.palette.text.primary
          }}>
            Resources
          </Typography>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                minHeight: 36,
                minWidth: 70,
                py: 0.25,
                px: 1
              },
              '& .MuiTabs-indicator': {
                height: 2,
                borderRadius: 1
              }
            }}
          >
            <Tab value="resources" label="Assets" />
            <Tab value="files" label="Files" />
            <Tab value="moodboard" label="Moodboard" />
            {FEATURE_FLAGS.resourcesLightingDiagram && (
              <Tab value="lighting" label="Lighting" />
            )}
          </Tabs>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {activeTab === 'resources' && (
            <Tooltip title="Add resource" arrow>
              <IconButton
                onClick={() => handleOpenDialog()}
                size="small"
                sx={{ color: theme.palette.primary.main }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {activeTab === 'resources' && (
        <>
      <ResourceGrid>
        {/* Filter & Sort Controls */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <StyledTextField size="small" placeholder="Search" value={query} onChange={(e)=>setQuery(e.target.value)} sx={{ width: 80 }} />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="rm-filter-type">Type</InputLabel>
            <Select labelId="rm-filter-type" label="Type" value={filterType} onChange={(e)=>{ setFilterType(e.target.value); }}>
              <MenuItem value="all">All</MenuItem>
              {typeOptions.map((t) => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="rm-filter-status">Status</InputLabel>
            <Select labelId="rm-filter-status" label="Status" value={filterStatus} onChange={(e)=>setFilterStatus(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending_approval">Pending</MenuItem>
              <MenuItem value="accepted">Approved</MenuItem>
              <MenuItem value="denied">Denied</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="rm-sort-field">Sort by</InputLabel>
            <Select labelId="rm-sort-field" label="Sort by" value={sortField} onChange={(e)=>setSortField(e.target.value)}>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="type">Type</MenuItem>
              <MenuItem value="date">Date</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="rm-sort-dir">Order</InputLabel>
            <Select labelId="rm-sort-dir" label="Order" value={sortDir} onChange={(e)=>setSortDir(e.target.value)}>
              <MenuItem value="asc">Asc</MenuItem>
              <MenuItem value="desc">Desc</MenuItem>
            </Select>
          </FormControl>
          <Chip size="small" label={`${applyFilterSort(resources).length} ${(applyFilterSort(resources).length === 1) ? 'item' : 'items'}`} />
        </Box>

        {Object.keys(groupResourcesByType(applyFilterSort(resources))).map((type) => (
          <Box key={type}>
            <CategoryHeader>
              <CategoryIcon sx={{ 
                fontSize: 24, 
                color: theme => theme.palette.mode === 'dark' ? '#90CAF9' : '#1976D2' 
              }} />
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                {groupResourcesByType(applyFilterSort(resources))[type].name}
              </Typography>
              <Chip 
                label={`${groupResourcesByType(applyFilterSort(resources))[type].resources.length}`} 
                size="small" 
                color="primary"
                sx={{ ml: 'auto' }}
              />
              <IconButton 
                size="small" 
                onClick={() => toggleCategory(type)}
                sx={{ ml: 1 }}
              >
                {collapsedCategories[type] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            </CategoryHeader>
            {!collapsedCategories[type] && (
              <ListContainer>
                {groupResourcesByType(applyFilterSort(resources))[type].resources.map((resource) => (
                  <ListItemContainer key={resource._id}>
                    <ListItemMainContent sx={{ alignItems: 'center' }}>
                      <CategoryIcon fontSize="small" color="action" />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1" fontWeight={500}>
                          {resource.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {resource.type} - {resource.subtype}
                        </Typography>
                        {resource.notes && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {resource.notes}
                          </Typography>
                        )}
                      </Box>
                      {resource.type === 'Studio' && (
                        <Chip
                          label={resource.status === 'pending_approval' ? 'Pending' : 
                                 resource.status === 'accepted' ? 'Approved' : 
                                 resource.status === 'denied' ? 'Denied' : 'Maintenance'}
                          size="small"
                          color={getStatusColor(resource.status)}
                          sx={{ 
                            mr: 1,
                            fontWeight: 'medium',
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      )}
                    </ListItemMainContent>
                    <ListItemSecondaryContent>
                      <IconButton size="small" onClick={() => handleOpenDialog(resource)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(resource._id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryContent>
                  </ListItemContainer>
                ))}
              </ListContainer>
            )}
          </Box>
        ))}
      </ResourceGrid>

      <StyledDialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            overflow: 'hidden',
            maxHeight: '90vh'
          }
        }}
      >
        <StyledDialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          alignItems: 'center',
          gap: 1,
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: '64px',
          letterSpacing: '0.5px'
        }}>
          {selectedType && (
            <Typography variant="h6" component="span" sx={{ mr: 1 }}>
              {RESOURCE_DETAILS[selectedType].icon}
            </Typography>
          )}
          <Typography variant="h6">
            {editingResource ? 'Edit Resource' : 'Add New Resource'}
          </Typography>
        </StyledDialogTitle>

        <StyledDialogContent dividers sx={{ pt: 3, pb: 2 }}>
          <Grid container spacing={3}>
            {/* Resource Type Selection */}
            <StyledFormField item xs={12}>
              <FormControl 
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': {
                    backgroundColor: 'background.paper',
                    px: 0.5,
                  },
                  mt: 3
                }}
              >
                <InputLabel id="resource-type-label">Resource Type</InputLabel>
                <Select
                  labelId="resource-type-label"
                  value={selectedType}
                  onChange={handleTypeChange}
                  label="Resource Type"
                >
                  {Object.keys(RESOURCE_TYPES).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </StyledFormField>

            {/* Subtype or Custom Name */}
            {selectedType && (
              <StyledFormField item xs={12}>
                {selectedType === 'Custom' ? (
                  <StyledTextField
                    fullWidth
                    label="Resource Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                ) : (
                  <FormControl fullWidth>
                    <InputLabel id="resource-subtype-label">Resource Subtype</InputLabel>
                    <Select
                      labelId="resource-subtype-label"
                      value={formData.subtype}
                      onChange={handleSubtypeChange}
                      label="Resource Subtype"
                    >
                      {RESOURCE_TYPES[selectedType].map((subtype) => (
                        <MenuItem key={subtype} value={subtype}>
                          {subtype}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </StyledFormField>
            )}

            {/* Time Mode Selector and Date/Time Fields */}
            <StyledFormField item xs={12}>
              <ToggleButtonGroup
                exclusive
                value={formData.allDay ? 'all_day' : 'time_range'}
                onChange={(e, val) => {
                  if (!val) return;
                  setFormData(prev => ({
                    ...prev,
                    allDay: val === 'all_day',
                    // Ensure sensible defaults when switching to time range
                    startTime: val === 'time_range' ? (prev.startTime || '09:00') : prev.startTime,
                    endTime: val === 'time_range' ? (prev.endTime || '17:00') : prev.endTime,
                  }));
                }}
                size="small"
              >
                <ToggleButton value="all_day">All day</ToggleButton>
                <ToggleButton value="time_range">Between times</ToggleButton>
              </ToggleButtonGroup>
            </StyledFormField>
            <StyledFormField item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Start Date"
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </StyledFormField>

            <StyledFormField item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="End Date"
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
                required
                inputProps={{
                  min: formData.startDate
                }}
              />
            </StyledFormField>

            {!formData.allDay && (
              <>
                <StyledFormField item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    label="Start Time"
                    type="time"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    required
                  />
                </StyledFormField>
                <StyledFormField item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    label="End Time"
                    type="time"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300, min: formData.startTime }}
                    required
                  />
                </StyledFormField>
              </>
            )}

            {/* Status Selection */}
            <StyledFormField item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value="pending_approval">
                    <Chip 
                      label="Pending Approval" 
                      size="small" 
                      color="secondary"
                      sx={{ mr: 1 }}
                    />
                  </MenuItem>
                  <MenuItem value="accepted">
                    <Chip 
                      label="Accepted" 
                      size="small" 
                      color="success"
                      sx={{ mr: 1 }}
                    />
                  </MenuItem>
                  <MenuItem value="denied">
                    <Chip 
                      label="Denied" 
                      size="small" 
                      color="error"
                      sx={{ mr: 1 }}
                    />
                  </MenuItem>
                  <MenuItem value="maintenance">
                    <Chip 
                      label="Maintenance" 
                      size="small" 
                      color="warning"
                      sx={{ mr: 1 }}
                    />
                  </MenuItem>
                </Select>
              </FormControl>
            </StyledFormField>

            {/* Dynamic Fields Based on Resource Type */}
            {selectedType && RESOURCE_DETAILS[selectedType].fields.map(field => (
              <StyledFormField item xs={12} sm={6} key={field}>
                <StyledTextField
                  fullWidth
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                  name={field}
                  value={formData[field]}
                  onChange={handleInputChange}
                  placeholder={`Enter ${field.toLowerCase().replace(/([A-Z])/g, ' $1')}`}
                />
              </StyledFormField>
            ))}

            {/* Notes Field */}
            <StyledFormField item xs={12}>
              <StyledTextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional details about the resource..."
              />
            </StyledFormField>
          </Grid>
        </StyledDialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              px: 2,
              color: (theme) => theme.palette.text.secondary,
              '&:hover': { backgroundColor: (theme) => alpha(theme.palette.action.hover, 0.5) }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            sx={{ 
              px: 3, 
              py: 1,
              borderRadius: 1,
              backgroundColor: (theme) => theme.palette.primary.main,
              color: (theme) => theme.palette.primary.contrastText
            }}
          >
            {editingResource ? 'Save Changes' : 'Add Resource'}
          </Button>
        </DialogActions>
      </StyledDialog>
      </>
      )}
      {activeTab === 'files' && (
        <Box sx={{ p: 3 }}>
          <ProjectFilesTab projectId={projectId} />
        </Box>
      )}
      {activeTab === 'moodboard' && (
        <Box sx={{ p: 3 }}>
          <ProjectMoodboardTab projectId={projectId} />
        </Box>
      )}
      {FEATURE_FLAGS.resourcesLightingDiagram && activeTab === 'lighting' && (
        <Box sx={{ p: { xs: 1.5, md: 3 } }}>
          <LightingDiagramTool projectId={projectId} />
        </Box>
      )}
</Box>
);
}

export default ResourceManagement;
